import React from 'react';
import { RefreshCw } from 'lucide-react';
import posthog from 'posthog-js';

/**
 * Error boundary component that catches rendering errors in child components.
 * Prevents a single component crash from taking down the entire application.
 * 
 * Usage:
 *   <ErrorBoundary name="SeedInventory">
 *     <SeedInventory />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error(`[ErrorBoundary:${this.props.name || 'Unknown'}] Component crashed:`, error, errorInfo);
    try {
      posthog.capture('component_error', {
        component: this.props.name || 'Unknown',
        error: error?.message || String(error),
        stack: error?.stack?.substring(0, 500),
      });
    } catch (e) {
      // PostHog may not be initialized; don't let analytics break error handling
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="max-w-xl mx-auto px-6 py-16 text-center">
          <div className="bg-white border-2 border-gray-100 rounded-2xl p-8">
            <div className="text-5xl mb-4">😵</div>
            <h2 className="text-xl font-bold text-[#2F5233] mb-2">
              Something went wrong
            </h2>
            <p className="text-gray-500 mb-6">
              {this.props.name
                ? `The ${this.props.name} page ran into an issue.`
                : 'This section ran into an issue.'
              }
              {' '}Try refreshing, and if it keeps happening, please let us know!
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={this.handleRetry}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#52A675] hover:bg-[#4A9168] text-white font-semibold rounded-xl transition-colors"
              >
                <RefreshCw size={16} />
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-[#2F5233] font-semibold rounded-xl transition-colors"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
